import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";
import DashboardLayout from "@/components/layout/DashboardLayout";
import Dashboard from "@/pages/Dashboard";
import AlphaCoreAI from "@/pages/AlphaCoreAI";
import MemorySettings from "@/pages/MemorySettings";
import MemoryOnboardingModal from "@/components/MemoryOnboardingModal";
import UpdateNotice from "@/components/UpdateNotice";
import KVisual from "@/pages/agents/KVisual";
import ResearchCenter from "@/pages/ResearchCenter";
import SwarmCenter from "@/pages/SwarmCenter";
import AlphaCoreCode from "@/pages/AlphaCoreCode";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import Files from "@/pages/Files";
import Billing from "@/pages/Billing";
import BillingSuccess from "@/pages/BillingSuccess";
import Settings from "@/pages/Settings";
import Support from "@/pages/Support";
import Websites from "@/pages/websites/Websites";
import WebsiteEditor from "@/pages/websites/WebsiteEditor";
import PublicSite from "@/pages/websites/PublicSite";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminModules from "@/pages/admin/AdminModules";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminRevenue from "@/pages/admin/AdminRevenue";
import AdminUsage from "@/pages/admin/AdminUsage";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminVideoEditor from "@/pages/admin/AdminVideoEditor";
import AdminTrialRequests from "@/pages/admin/AdminTrialRequests";
import AdminPlanApplications from "@/pages/admin/AdminPlanApplications";
import AdminVideoAccess from "@/pages/admin/AdminVideoAccess";
import AdminPayments from "@/pages/admin/AdminPayments";
import VideoGenerator from "@/pages/VideoGenerator";
import AlphaCoreLogo from "@/components/AlphaCoreLogo";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#07080B]">
      <div className="flex flex-col items-center gap-6">
        <div className="kr-pulse-soft">
          <AlphaCoreLogo variant="hero" size={140} />
        </div>
        <div className="font-mono text-sm text-white/60 kr-pulse-soft" data-testid="loading">INITIALIZING ALPHACORE…</div>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      {user && <MemoryOnboardingModal />}
      <UpdateNotice />
      <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/site/:slug" element={<PublicSite />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Dashboard />} />
        {/* The six launch modules */}
        <Route path="/agents/create" element={<AlphaCoreAI />} />
        <Route path="/images" element={<KVisual />} />
        <Route path="/websites" element={<Websites />} />
        <Route path="/websites/:id" element={<WebsiteEditor />} />
        <Route path="/research" element={<ResearchCenter />} />
        <Route path="/swarm" element={<SwarmCenter />} />
        <Route path="/code" element={<AlphaCoreCode />} />
        <Route path="/video" element={<VideoGenerator />} />
        {/* Legacy route redirects (modules renamed or archived) */}
        <Route path="/agents/alphacore-ai" element={<Navigate to="/agents/create" replace />} />
        <Route path="/agents/k-create" element={<Navigate to="/agents/create" replace />} />
        <Route path="/agents/create-legacy" element={<Navigate to="/agents/create" replace />} />
        <Route path="/agents/k-visual" element={<Navigate to="/images" replace />} />
        <Route path="/agents/k-research" element={<Navigate to="/research" replace />} />
        <Route path="/core-swarm" element={<Navigate to="/swarm" replace />} />
        <Route path="/claw" element={<Navigate to="/swarm" replace />} />
        <Route path="/agents/k-video" element={<Navigate to="/video" replace />} />
        <Route path="/agents/k-social" element={<Navigate to="/dashboard" replace />} />
        <Route path="/agents/k-insight" element={<Navigate to="/dashboard" replace />} />
        <Route path="/video-editor" element={<Navigate to="/dashboard" replace />} />
        <Route path="/video-editor/:id" element={<Navigate to="/dashboard" replace />} />
        <Route path="/generate" element={<Navigate to="/dashboard" replace />} />
        {/* Supporting areas */}
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:id" element={<ProjectDetail />} />
        <Route path="/files" element={<Files />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/support" element={<Support />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/settings/memory" element={<MemorySettings />} />
      </Route>
      <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
        <Route path="/admin" element={<AdminOverview />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/users/:id" element={<AdminUserDetail />} />
        <Route path="/admin/modules" element={<AdminModules />} />
        <Route path="/admin/analytics" element={<AdminAnalytics />} />
        <Route path="/admin/revenue" element={<AdminRevenue />} />
        <Route path="/admin/usage" element={<AdminUsage />} />
        <Route path="/admin/video-editor" element={<AdminVideoEditor />} />
        <Route path="/admin/video-access" element={<AdminVideoAccess />} />
        <Route path="/admin/payments" element={<AdminPayments />} />
        <Route path="/admin/plan-applications" element={<AdminPlanApplications />} />
        <Route path="/admin/trial-requests" element={<AdminTrialRequests />} />
        <Route path="/admin/tickets" element={<AdminTickets />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App font-body">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#101219",
                border: "1px solid rgba(255,255,255,0.08)",
                color: "#fff",
                fontFamily: "Manrope, sans-serif",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
